import React from 'react';
import { Button, Typography, Card, Space, Spin } from 'components';
import { CheckCircleFilled, LoadingOutlined, PrinterOutlined } from '@ant-design/icons';
import { DimensionInputs } from 'components/warehouse';
import { useFullPackageAndDimensions } from './useFullPackageAndDimensions';
import { ProgressStepper } from '../components/ProgressStepper';
import { LabelRequestIcon } from 'components/common/icons/warehouse/LabelRequestIcon';
import styles from './styles.module.scss';

const { Title, Text } = Typography;

export const FullPackageAndDimensions: React.FC = () => {
  const {
    isLoading,
    currentPackageUuid,
    currentPackageIndex,
    totalPackages,
    dimensions,
    areDimensionsValid,
    isDownloadingNote,
    isGeneratingLabel,
    labelGenerationStatus,
    handleDimensionChange,
    handleUnitChange,
    handlePrintDeliveryNote,
    handleGenerateLabel,
    handleContinue,
    handleBack,
    completedCount,
    totalCount,
  } = useFullPackageAndDimensions();

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  // EXW Flow - Label Request is sent to the Client
  if (labelGenerationStatus === 'exw') {
    return (
      <div className={styles.container}>
        <ProgressStepper
          currentStep={4}
          totalSteps={4}
          completedCount={completedCount}
          totalCount={totalCount}
          onBack={handleBack}
        />

        <div className={styles.statusScreen}>
          <div className={styles.statusIconContainer}>
            <LabelRequestIcon />
          </div>

          <Title level={3} className={styles.statusTitle}>
            Label Request is sent to the Client
          </Title>

          <Text className={styles.statusText}>
            {`Please wait for the client to generate and send the label. You'll be notified once it's ready. This may take
              couple hours.`}
          </Text>
        </div>

        <div className={styles.bottomActions}>
          <Button type="primary" size="large" onClick={handleContinue} block>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Generating Flow - Your request is being generated
  if (labelGenerationStatus === 'generating') {
    return (
      <div className={styles.container}>
        <ProgressStepper
          currentStep={4}
          totalSteps={4}
          completedCount={completedCount}
          totalCount={totalCount}
          showBackButton={false}
        />

        <div className={styles.statusScreen}>
          <div>
            <Spin indicator={<LoadingOutlined className={styles.statusIconLarge} spin />} />
          </div>

          <Title level={3} className={styles.statusTitle}>
            Your request is being generated
          </Title>

          <Text className={styles.statusText}>Please wait till label generated, This can take couple of minutes.</Text>

          <Button
            type="link"
            size="large"
            icon={<PrinterOutlined />}
            onClick={handlePrintDeliveryNote}
            loading={isDownloadingNote}
            className={styles.printLink}
          >
            Print Delivery Note
          </Button>
        </div>
      </div>
    );
  }

  // Default Flow - Package Dimensions
  return (
    <div className={styles.container}>
      {/* Progress Stepper */}
      <ProgressStepper
        currentStep={4}
        totalSteps={4}
        completedCount={completedCount}
        totalCount={totalCount}
        onBack={handleBack}
      />

      <div className={styles.content}>
        {/* Success Icon */}
        <div className={styles.successIcon}>
          <CheckCircleFilled />
        </div>

        {/* Title */}
        <Title level={3} className={styles.pageTitle}>
          Your Package is full
        </Title>

        {/* Subtitle */}
        <Text className={styles.subtitle}>Proceed to shipping to complete the process.</Text>

        {/* Order Info Card */}
        <Card className={styles.orderCard}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Text className={styles.orderId}>Order ID: {currentPackageUuid}</Text>
            {totalPackages > 1 && (
              <Text className={styles.packageIndicator}>
                Package {currentPackageIndex + 1} of {totalPackages}
              </Text>
            )}
          </Space>
        </Card>

        {/* Dimensions Section */}
        <div className={styles.dimensionsSection}>
          <DimensionInputs
            title="PACKAGE SIZES"
            dimensions={dimensions}
            onDimensionChange={handleDimensionChange}
            onUnitChange={handleUnitChange}
          />
        </div>
      </div>

      {/* Bottom Actions */}
      <div className={styles.bottomActions}>
        <Button
          type="default"
          size="large"
          onClick={handlePrintDeliveryNote}
          className={styles.printButton}
          loading={isDownloadingNote}
          disabled={isGeneratingLabel}
          block
        >
          Print Delivery Note
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleGenerateLabel}
          disabled={!areDimensionsValid}
          loading={isGeneratingLabel}
          className={styles.generateButton}
          block
        >
          Generate Label
        </Button>
      </div>
    </div>
  );
};
